/**
 * Os cinco atos da devolutiva (#852).
 *
 * A tela deixou de ser cinco abas equivalentes e virou um roteiro com ordem e
 * clímax. A diferença não é de aparência: aba é lugar, ato é passo. O Ato 5 só
 * abre por ação do vendedor, e uma vez aberto continua aberto na sessão.
 *
 * Os ids são estáveis e não aparecem na tela; o que o cliente lê vem do i18n.
 */
export const ACT_IDS = [
  "environment",
  "diagnosis",
  "evidence",
  "treatment",
  "investment",
] as const;

export type ActId = (typeof ACT_IDS)[number];

/** O ato de abertura. Recarregar a página volta para cá, sempre. */
export const FIRST_ACT: ActId = ACT_IDS[0];

/** O ato trancado. É o único com sub-rótulo que muda de estado. */
export const FINAL_ACT: ActId = ACT_IDS[ACT_IDS.length - 1];

/**
 * Atos fora do roteiro (#1476).
 *
 * O Ato 3, "As provas", saiu da apresentação em 19/10/2026. Ele traz Simplification
 * Item, que só tem sentido na conversão para S/4HANA Private, e o GROW é
 * reimplementação. O id continua no catálogo para o ato voltar sem mexer em
 * quem o referencia; o que muda é a sequência que a tela percorre.
 */
export const HIDDEN_ACTS: ReadonlySet<ActId> = new Set<ActId>(["evidence"]);

/** A sequência que a tela mostra, numera e percorre. */
export const VISIBLE_ACT_IDS: readonly ActId[] = ACT_IDS.filter((id) => !HIDDEN_ACTS.has(id));

/**
 * A aba "Resumo · para o executivo" (EF-DIAG-004).
 *
 * Mora na barra dos atos, mas **não é um ato**: não tem número, não entra no
 * "Act N of M", não tem abertura, notas nem rodapé de navegação. Por isso fica
 * fora de `ACT_IDS` e de tudo que percorre o roteiro.
 */
export const SUMMARY_TAB = "summary" as const;

/** O que a barra pode ter ativo: um ato ou o resumo. */
export type DiagnosticTab = ActId | typeof SUMMARY_TAB;

/**
 * Onde o resumo fica na barra. Proposta da EF: antes do Ato 1, como abertura da
 * apresentação. Ponto em aberto com o Marco, então a posição é só esta constante.
 */
export const SUMMARY_TAB_POSITION: "before" | "after" = "before";

/** A ordem da barra: os atos visíveis, com o resumo na posição da constante. */
export const TAB_IDS: readonly DiagnosticTab[] =
  SUMMARY_TAB_POSITION === "before"
    ? [SUMMARY_TAB, ...VISIBLE_ACT_IDS]
    : [...VISIBLE_ACT_IDS, SUMMARY_TAB];

/** 1 a N, para a copy, na sequência visível. O índice é detalhe de implementação. */
export function actNumber(act: ActId): number {
  return VISIBLE_ACT_IDS.indexOf(act) + 1;
}

export function actBefore(act: ActId): ActId | undefined {
  return VISIBLE_ACT_IDS[VISIBLE_ACT_IDS.indexOf(act) - 1];
}

export function actAfter(act: ActId): ActId | undefined {
  return VISIBLE_ACT_IDS[VISIBLE_ACT_IDS.indexOf(act) + 1];
}

/**
 * Rótulo e sub-rótulo de cada ato, como chaves de i18n.
 *
 * O sub-rótulo do Ato 5 tem dois estados, e é isso que anuncia a trava antes de
 * alguém clicar: `a abrir` enquanto fechado, `os números` depois. O desenho põe
 * essa troca no `act5Sub`.
 */
export const ACT_LABELS: Record<ActId, { label: string; sub: string }> = {
  environment: { label: "The environment", sub: "what exists" },
  diagnosis: { label: "The diagnosis", sub: "what it means" },
  evidence: { label: "The evidence", sub: "we know how to treat it" },
  treatment: { label: "The treatment", sub: "how we run it" },
  investment: { label: "Investment", sub: "to open" },
};

/** O sub-rótulo do Ato 5 depois de revelado. */
export const FINAL_ACT_OPENED_SUB = "the numbers";

/**
 * A abertura de cada ato: o que o vendedor diz antes de mostrar qualquer número.
 *
 * É a espinha do roteiro, e a razão de a devolutiva ser uma sequência e não cinco
 * abas. Transcrito do desenho aprovado, com duas correções deliberadas:
 *
 * 1. O nome do cliente do mock saiu. Ele não trafega no payload do Diagnóstico, e
 *    escrever um nome fictício na tela do consultor seria pior do que não ter.
 * 2. O **SCMON saiu da lista de fontes** do Ato 1. O desenho o cita em três
 *    lugares, mas hoje o endpoint `/abap-call-monitor` não tem consumidor: quem
 *    mede execução é a cascata do ST03N. Afirmar a fonte que não lemos é
 *    exatamente o tipo de mentira que esta tela existe para não contar. Volta
 *    junto com a #839.
 */
export const ACT_OPENING: Record<ActId, { heading: string; intro: string }> = {
  environment: {
    heading: "We looked inside. This is what exists.",
    intro:
      "Before any proposal, the agents read the SAP landscape. Nothing here is a market estimate: it is the portrait of the system, extracted from ST03N, SCMON, the ABAP repository and SU01.",
  },
  diagnosis: {
    heading: "What this means.",
    intro:
      "A qualitative reading of the portrait: where the complexity sits, what is standard and what needs a decision. Automatic classification, validated by a consultant.",
  },
  // O título perdeu a segunda oração em 24/09/2026, na reunião de validação:
  // nem todo diagnóstico vira migração. Há caso em que o cliente só quer ler o
  // próprio ambiente (Clean Core, sem GROW), e prometer tratamento ali é
  // vender o que não se combinou.
  evidence: {
    heading: "We understand your pain.",
    intro:
      "A few of the mapped points, chosen for depth of understanding and for having a treatment path.",
  },
  treatment: {
    heading: "How we run it from here.",
    intro:
      "The journey to SAP GROW through the Exed methodology with AI agents. The agents do the heavy lifting; your people decide; everything is recorded.",
  },
  investment: {
    heading: "The numbers, all at once.",
    intro:
      "Investment and timeline calculated from the real landscape, not from a market table.",
  },
};

/**
 * As cinco fases da metodologia, para a linha do tempo do Ato 4.
 *
 * **É copy de metodologia, não dado.** Não há campo no payload para prazo por
 * fase, e o card declara isso no rodapé em vez de fingir procedência. O desenho
 * mostra "Prepare 1,0 · Explore 2,5 · Realize 3,5 · Deploy 1,5", e esses números
 * não têm produtor: ficam de fora até terem.
 */
export const ACTIVATE_PHASES = [
  {
    key: "discover",
    phase: "Discover · done",
    title: "Diagnosis",
    body: "The agents read the landscape with no GROW tenant needed. This briefing: you are here.",
  },
  {
    key: "prepare",
    phase: "Prepare",
    title: "Foundation",
    body: "GROW tenant provisioned, Starter System live, team and governance mobilised.",
  },
  {
    key: "explore",
    phase: "Explore",
    title: "Fit-to-Standard",
    body: "Workshops assisted by the agents; users already training on the Starter alongside the configuration.",
  },
  {
    key: "realize",
    phase: "Realize",
    title: "Build",
    body: "Clean Core extensions, data through the Migration Cockpit, integrations on the Integration Suite, tests with evidence.",
  },
  {
    key: "deploy",
    phase: "Deploy",
    title: "Go-live",
    body: "Planned cutover, assisted go-live and hypercare with users already fluent in the new environment.",
  },
] as const;
